import {flatten, get, has, isEmpty, isNil} from 'lodash'
import {api} from 'services/api'
import theme from 'theme/index'
import QuoteHelper, {getCalculatedOrderItem, OrderReasonType, QuoteStatus, ReferralType} from 'helpers/quotes'
import {features, featureToggles, permissions, ScalingType} from 'helpers/constants'
import {accountHasFeature, featureToggleIsOn, userHasFeature, userHasPermission} from 'helpers/user'
import uuid, {isUUID} from '../helpers/uuid'
import {canReturnDeliveryNotes, DeliveryStatus} from 'helpers/deliveries'
import {canInvoiceBeforeDelivered} from 'helpers/invoices'
import {getPurchaseOrderedQuantityForOrderItem} from 'helpers/purchasing/purchaseOrderItems'
import {reorderItems} from 'helpers/arrays'
import {PartScalingType} from 'helpers/pricingTemplateItems'
import {getPartSupplierForCompany} from 'helpers/partSuppliers'
import {isComplexPricingEnabled} from 'helpers/pricing'
import {formatRoundedNumber, getTotalValue} from 'helpers/financial'
import {getSubItemHeight, getSubItemLength, getSubItemWidth} from 'helpers/quoting/subItems'
import {setEntityEdited} from 'helpers/pivotTable'
import {parseCssSelector} from "helpers/css"
import {
  findDefaultSupplierId,
  getDefaultPack,
  getDefaultSupplierIdForPack,
  getPartPackFor,
  getPartPacks,
  getPartPackSupplierOption,
  partPackHasSuppliers,
} from 'helpers/packs'
import {
  canEditPartType,
  canUsePack,
  getMinPackQuantity,
  getPackTypeName,
  getPartSaleRate,
  PartType,
} from 'helpers/parts'
import {
  getColorForJobCardStatus,
  getJobBoardStatusName,
  isJobCardComplete,
  isOrderProductionComplete,
  JobBoardStatus,
} from 'helpers/production'
import {
  getCalculatorNameForItem,
  isPricingCalculatorActive,
  isPricingCalculatorEntity,
  PRICING_METHODS,
  setPricingMethod,
} from "helpers/pricingCalculator"

// Status-change actions offered on the order status dropdown (the `jobStatus`
// values produced by getOrderStatusOptions and handled by the status dialogs).
export const JobStatusAction = {
  quoteVerbal: 'quote_verbal',
  quoteFormal: 'quote_formal',
  confirm: 'confirm',
  reject: 'reject',
  rejectAll: 'reject_all',
  unreject: 'unreject',
  close: 'close',
  invoice: 'invoice',
  fullyInvoiced: 'fully_invoiced',
  deliver: 'deliver',
  unconfirm: 'unconfirm',
  credit: 'credit',
  paid: 'paid',
  reopen: 'reopen',
  return: 'return',
}

export const PriceMode = {
  default: 0,
  fixed_discount: 1,
  pc_discount: 2,
  fixed_price: 3,
  fixed_margin: 4,
}

export const isFixedPrice = (priceMode) => {
  return priceMode === PriceMode.fixed_price
}

export const OrderItemType = {
  entered: 0,
  part: 1,
  builder: 2,
  calc_wizard: 3,
  pricing_template: 4,
  pricing_calculator: 5,
}

export const PricingCalculatorContext = {
  QUOTING: 0,
  BUILDER: 1,
}

export {ScalingType}

export const getScalingType = (id) => {
  return Object.values(ScalingType).find(t => t.id === id)
}

export const getAllScalingTypes = () => {
  return Object.values(ScalingType).map(type => ({
    key: type.id,
    value: type.id,
    label: type.label,
  }))
}

export const OrderItemPricingMode = {
  part: 0,
  contents: 1,
}

export const canUserManageOwnership = (userContactId, order) => {
  return (userContactId === order.estimatorId
    || userContactId === order.salesmanId
    || userContactId === order.creatorId
    || (order.contactId && order.contact.accountManagerId === userContactId))
}

export const recalculateTaxRate = taxRate => taxRate * 100

export const canAddItemFromPricingTemplate = (user) => {
  return accountHasFeature(user, features.PRICE_LISTS_LICENCE)
}

export const canAddItemOfType = (type, user) => {
  switch (type) {
    case OrderItemType.pricing_calculator:
      return userHasPermission(user, permissions.ENTER_ITEM)
    case OrderItemType.entered:
      return userHasPermission(user, permissions.ENTER_ITEM)

    case OrderItemType.builder:
      return userHasPermission(user, permissions.ENTER_ITEM)
        && accountHasFeature(user, features.PRICE_LISTS_LICENCE)

    case OrderItemType.part:
    case OrderItemType.pricing_template:
      return accountHasFeature(user, features.PRICE_LISTS_LICENCE)

    default:
      return false
  }
}

export const isContainerItem = (item) =>
  [OrderItemType.builder, OrderItemType.pricing_template].includes(get(item, 'type.id')) ||
  get(item, 'partType') === PartType.ASSEMBLY.id

export const canAddItem = (item, user) => {
  if (!item) return false
  if (isContainerItem(item)) {
    const subItems = get(item, 'subItems') || []
    return canAddItemOfType(get(item, 'type.id'), user) && subItems.every(subItem => canAddItem(subItem, user))
  }
  return canAddItemOfType(get(item, 'type.id'), user)
}

export const canEditItem = (item, user, allItems = null) => {
  if (!item) return false

  const type = get(item, 'type.id')
  let ret

  // Always allow EDITING of manually added line items
  if (type === OrderItemType.entered ||
    (type === OrderItemType.builder && !item.partId) ||
    (type === OrderItemType.pricing_template && !item.partId)) {
    ret = true

  } else {

    switch (type) {
      case OrderItemType.entered:
        ret = userHasPermission(user, permissions.ENTER_ITEM)
        break

      case OrderItemType.builder:
        if (item.partId) {
          ret = accountHasFeature(user, features.PRICE_LISTS_LICENCE)
        } else {
          ret = userHasPermission(user, permissions.ENTER_ITEM)
        }
        break

      case OrderItemType.part:
        ret = accountHasFeature(user, features.PRICE_LISTS_LICENCE)
        break

      case OrderItemType.pricing_calculator:
        ret = featureToggleIsOn(user, featureToggles.PRICING_CALCULATOR)
        break

      default:
        ret = false
        break
    }
  }

  const parent = item.parentId && allItems
    ? allItems.find(i => i.id === item.parentId)
    : null

  // check recursively upwards (all parents must be editable too)
  if (ret && parent) ret = canEditItem(parent, user, allItems)

  return ret
}

const lineItemReadOnlyFields = ['scalingType']
const subItemReadOnlyFields = ['discount', 'taxCode', 'nominalCode', 'referralFee']

export const isChildOfPricingCalculator = (item, allItems) => {
  if (!allItems || !item.parentId) return false

  const parent = allItems.find(i => i.id === item.parentId)
  if (parent) return get(parent, 'type.id') === OrderItemType.pricing_calculator

  // Fallback: find any pricing calculator that has this item in its subItems
  // This is required as parent id isn't set in a price builder until the builder is saved
  return allItems.some(i =>
    get(i, 'type.id') === OrderItemType.pricing_calculator
    && i.subItems
    && i.subItems.some(si => si.id === item.id)
  )
}

export const areAllItemsSaved = (items) => items.every(isItemSaved)

export const isItemSaved = (item) => {
  return !isUUID(item.id) && item.id >= 0
}

export const canEditItemInQuoteItemGrid = (
  item,
  column,
  user,
  allItems = null,
  calculatorEditableColumns = null
) => {
  return canEditItemInGrid(item, column, user, allItems, false, !isNil(item.parentId), calculatorEditableColumns)
}

export const canEditItemInSubItemGrid = (
  item,
  column,
  user,
  allItems = null,
  allowUnsavedItems = false,
  readOnly = false,
  calculatorEditableColumns = null
) => {
  return !readOnly &&
    (!!item.isNewRow
      || (!!item.enabled
        && canEditItemInGrid(item, column, user, allItems, allowUnsavedItems, true, calculatorEditableColumns)))
}

export const canEditItemInGrid = (
  item,
  column,
  user,
  allItems = null,
  allowUnsavedItems = false,
  isSubItem = false,
  calculatorEditableColumns = null
) => {
  if (!allowUnsavedItems && !item.isNewRow && !isItemSaved(item)) return false
  const columnKey = get(column, 'key', '')

  if (column && !isSubItem && lineItemReadOnlyFields.includes(columnKey)) return false
  if (column && isSubItem && subItemReadOnlyFields.includes(columnKey)) return false
  if (column && column.user && isSubItem) return false

  if (item.isNewRow) {
    // If not allowed to enter item, don't allow adding via anything other than part code or desc lookup
    return !column || ['partCode', 'desc'].includes(columnKey) || canEnterItem(user)
  }

  // cannot edit part code for price list items
  if (item && item.partId && columnKey === 'partCode') return false

  const partTypeId = get(item, 'partType', null)
  if (item && !canEditPartType(partTypeId) && columnKey === 'partType') return false

  // cannot edit supplier if no suppliers
  if (item
    && columnKey === 'supplierCompanyId'
    && !hasPartPackForSuppliers(item, isComplexPricingEnabled(user))) {
    return false
  }

  // Subitems of a pricing calculator are driven entirely by the calculator-service
  // result and should never be editable. Runs regardless of column so the checkbox
  // path (onCanEdit called without a column) also disables correctly.
  if (isChildOfPricingCalculator(item, allItems)) return false

  if (isPricingCalculatorEntity(item) && column && calculatorEditableColumns) {
    if (!isPricingCalculatorActive(user, getCalculatorNameForItem(item)))
      return false

    if (!calculatorEditableColumns.includes(columnKey))
      return false
  }

  return canEditItem(item, user, allItems)
}

export const getType = (part) => {
  if (part) {
    switch (part.type) {
      case PartType.ASSEMBLY.id:
        return OrderItemType.builder
      case PartType.PRO_CALC_WIZARD.id:
        return OrderItemType.calc_wizard
      case PartType.GO_PRICING_CALCULATOR.id:
        return OrderItemType.pricing_calculator
      default:
        return OrderItemType.part
    }
  }
  return OrderItemType.entered
}

export const showSubItems = (orderItem, part) => {
  return get(orderItem, 'type.id') === OrderItemType.builder ||
    get(orderItem, 'type.id') === OrderItemType.pricing_template ||
    get(orderItem, 'type.id') === OrderItemType.calc_wizard ||
    get(orderItem, 'type.id') === OrderItemType.pricing_calculator ||
    get(part, 'type') === PartType.ASSEMBLY.id
}

export const orderItemIsPartType = (orderItem) => {
  return !isNil(orderItem.partId) && !isNil(orderItem.partPackId)
}

export const orderItemIsBuiltType = (orderItem) => {
  return [
      OrderItemType.builder,
      OrderItemType.pricing_template,
      OrderItemType.calc_wizard,
      OrderItemType.pricing_calculator,
    ].includes(get(orderItem, 'type.id')) ||
    get(orderItem, 'partType') === PartType.ASSEMBLY.id
}

export const isStockableItem = (orderItem) =>
  [
    PartType.MATERIAL.id,
    PartType.OUTSOURCED.id,
  ].includes(get(orderItem, 'partType'))

export const canEditItemPartType = (orderItem, part) => {
  if (!canEditPartType(orderItem.partType)) return false
  if (part) return false
  return !showSubItems(orderItem, part)
}

export const round = (value, decimals) => {
  if (isNil(value) || isNil(decimals)) {
    throw new Error('Invalid arguments')
  } else {
    return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals)
  }
}

export const getPercentage = (value, percentage) => {
  if (isNil(value) || isNil(percentage)) {
    throw new Error('Invalid argument')
  } else {
    return (value * percentage) / 100
  }
}

export const getDiscount = (value, discount) => {
  if (isNil(value) || isNil(discount)) {
    throw new Error('Invalid argument')
  } else {
    return value - getPercentage(value, discount)
  }
}

export const getPercentageReduction = (value, discount) => {
  if (isNil(value) || isNil(discount)) {
    throw new Error('Invalid argument')
  } else {
    if (value !== 0) {
      return (discount * 100) / value
    } else {
      return 0
    }
  }
}

export const getLineItemTotalPrice = (order, orderItem) => {
  if (isNil(order) || isNil(orderItem)) {
    throw new Error('Invalid argument')
  } else {

    let lineItemTotalPrice = orderItem.unitPrice

    // Apply Customer discount
    if (has(orderItem, 'discount') && !isNil(orderItem['discount']) && orderItem['discount'] !== 0) {
      lineItemTotalPrice = round(getDiscount(lineItemTotalPrice, orderItem['discount']), 2)
    }

    // Apply Referral fee
    if (
      has(orderItem, 'referralValue')
      && !isNil(orderItem['referralValue'])
      && orderItem['referralValue'] !== 0
    ) {
      lineItemTotalPrice = round(
        (lineItemTotalPrice + getPercentage(lineItemTotalPrice, orderItem['referralValue'])),
        2
      )
    }

    // Calculate line item by quantity
    if (orderItem['quantity'] !== 0) {
      lineItemTotalPrice = lineItemTotalPrice * orderItem['quantity']
    }

    lineItemTotalPrice = round(lineItemTotalPrice, 2)

    // Apply Order Discount
    if (has(order, 'discountPc') && !isNil(order['discountPc'])) {
      lineItemTotalPrice = round(getDiscount(lineItemTotalPrice, order['discountPc']), 2)
    }

    return lineItemTotalPrice
  }
}

export const getLineItemsTotalPrice = (order, orderItems, lineItemTaxRounding) => {
  if (isNil(order) || isNil(orderItems) || isNil(lineItemTaxRounding)) {
    throw new Error('Invalid argument')
  } else {
    let lineItemsTotalPrice = 0

    orderItems.map((orderItem) => {
      lineItemsTotalPrice = lineItemsTotalPrice + getLineItemTotalPrice(order, orderItem)
    })

    lineItemsTotalPrice = round(lineItemsTotalPrice, 2)

    if (order['priceMode']['id'] === PriceMode.fixed_margin) {

      // Fixed margin
      lineItemsTotalPrice = lineItemsTotalPrice + getPercentage(lineItemsTotalPrice, order['margin'])

    } else if (order['priceMode']['id'] === PriceMode.fixed_price) {
      lineItemsTotalPrice = order['totalPrice']
    }

    return lineItemsTotalPrice = round(lineItemsTotalPrice, 2)
  }
}

export const getLineItemTax = (order, orderItem, lineItemTaxRounding) => {
  if (isNil(order) || isNil(orderItem) || isNil(lineItemTaxRounding)) {
    throw new Error('Invalid argument')
  } else {

    let lineItemTax = 0
    const lineItemTotalPrice = getLineItemTotalPrice(order, orderItem)

    if (has(orderItem, 'taxRate') && !isNil(orderItem['taxRate']) && orderItem['taxRate'] !== 0) {

      lineItemTax = getPercentage(lineItemTotalPrice, recalculateTaxRate(orderItem['taxRate']))
    } else if (has(order, 'taxRate') && !isNil(order['taxRate']) && order['taxRate'] !== 0) {
      lineItemTax = getPercentage(lineItemTotalPrice, recalculateTaxRate(order['taxRate']))
    }

    if (lineItemTaxRounding === true) {
      lineItemTax = round(lineItemTax, 2)
    }

    return lineItemTax
  }
}

export const getLineItemsTotalTax = (order, orderItems, lineItemTaxRounding) => {
  if (isNil(order) || isNil(orderItems) || isNil(lineItemTaxRounding)) {
    throw new Error('Invalid argument')
  } else {
    let totalLineItemsTax = 0

    orderItems.map((orderItem) => {
      totalLineItemsTax = totalLineItemsTax + getLineItemTax(order, orderItem, lineItemTaxRounding)
    })

    totalLineItemsTax = round(totalLineItemsTax, 2)

    if (order['priceMode']['id'] === PriceMode.fixed_margin) {
      // Fixed margin

      // Compute total price from total cost plus fixed margin
      const margin = getPercentage(order['totalCost'], order['margin'])
      const totalPrice = order['totalCost'] + margin


      totalLineItemsTax = totalLineItemsTax + (totalPrice * recalculateTaxRate(order['taxRate']))
    } else if (order['priceMode']['id'] === PriceMode.fixed_price) {

      const totalPrice = order['totalPrice']
      totalLineItemsTax = totalLineItemsTax + (totalPrice * recalculateTaxRate(order['taxRate']))
    }

    return round(totalLineItemsTax, 2)
  }
}

export const getLineItemsTotalPriceIncludingTax = (order, orderItems, lineItemTaxRounding) => {
  if (isNil(order) || isNil(orderItems) || isNil(lineItemTaxRounding)) {
    throw new Error('Invalid argument')
  } else {

    const totalLineItemsPrice = getLineItemsTotalPrice(order, orderItems, lineItemTaxRounding)
    const totalLineItemsTax = getLineItemsTotalTax(order, orderItems, lineItemTaxRounding)
    if (totalLineItemsPrice !== 0) {
      return round(totalLineItemsPrice + totalLineItemsTax, 2)
    } else {
      return 0
    }
  }
}

export const getLineItemsTotalCost = (orderItems) => {
  if (isNil(orderItems)) {
    throw new Error('Invalid argument')
  } else {
    let lineItemsTotalCost = 0
    orderItems.map((orderItem) => {
      lineItemsTotalCost = lineItemsTotalCost + orderItem['totalCost']
    })
    return round(lineItemsTotalCost, 2)
  }
}

export const getLineItemsMargin = (order, orderItems, lineItemTaxRounding) => {
  if (isNil(orderItems)) {
    throw new Error('Invalid argument')
  } else {
    const lineItemsTotalPrice = getLineItemsTotalPrice(order, orderItems, lineItemTaxRounding)
    const lineItemsTotalCost = getLineItemsTotalCost(orderItems)
    const margin = ((lineItemsTotalPrice - lineItemsTotalCost) / lineItemsTotalPrice) * 100
    return round(margin, 2)
  }
}

export const getLineItemsProfit = (order, orderItems, lineItemTaxRounding) => {
  if (isNil(orderItems)) {
    throw new Error('Invalid argument')
  } else {
    const lineItemsTotalPrice = getLineItemsTotalPrice(order, orderItems, lineItemTaxRounding)
    const lineItemsTotalCost = getLineItemsTotalCost(orderItems)
    const profit = lineItemsTotalPrice - lineItemsTotalCost
    return round(profit, 2)
  }
}

export const getMargin = (totalPrice, totalCost) => {
  if (isNil(totalPrice) || isNil(totalCost)) {
    throw new Error('Invalid argument')
  } else {
    return round(((totalPrice - totalCost) / totalPrice) * 100, 2)
  }
}

export const getNetMargin = (netPrice, totalCost) => {
  if (isNil(netPrice) || isNil(totalCost)) {
    throw new Error('Invalid argument')
  } else {
    return round(((netPrice - totalCost) / netPrice) * 100, 2)
  }
}

export const isOrderReason = (orderReason) => {
  return orderReason.type.id === OrderReasonType.order
}

export const isRejectReason = (orderReason) => {
  return orderReason.type.id === OrderReasonType.reject
}

export const getOrderReasons = (orderReasons) => {
  return isNil(orderReasons)
    ? []
    : orderReasons.filter(isOrderReason)
}

export const getRejectedOrderReasons = (orderReasons) => {
  return isNil(orderReasons)
    ? []
    : orderReasons.filter(isRejectReason)
}

export const getOrderReasonOptions = (orderReasons) => {
  return orderReasons.map(orderReason => ({
    key: orderReason.id,
    value: orderReason.id,
    label: orderReason.name,
  }))
}

export const isMarkJobAsDeliveredDisabled = (proSync, order) => {
  if (!proSync && order.jobStatus === null)
    return false

  return proSync || !isOrderProductionComplete(order)
}

export const getOrderStatusOptions = (user, order, status, revision, nextRevision, proSync, quoteIsEmpty) => {
  if (isNil(status) || isNil(proSync)) {
    return []
  } else {
    const canConfirm = userHasPermission(user, permissions.CONFIRM_JOBS)
    const canDeliver = userHasFeature(user, features.DELIVERIES_LICENCE)
      && userHasPermission(user, permissions.MANAGE_DELIVERY_NOTES)
    const canReturn = canReturnDeliveryNotes(user)
    const canInvoice = userHasFeature(user, features.INVOICING_LICENCE)
      && userHasPermission(user, permissions.EDIT_INVOICES)
    const canMarkInvoiced = userHasFeature(user, features.INVOICING_LICENCE)
      && userHasPermission(user, permissions.MARK_INVOICED)

    const returnsAvailable = canReturn && hasAnyDelivery(order)

    switch (status) {
      case QuoteStatus.draft: {
        if (quoteIsEmpty) {
          return [{
            className: 'change-order-status-close',
            key: 'close',
            value: 'close',
            label: 'Close',
            clearable: false,
          }]
        }
        const ret = [
          {
            className: 'change-order-status-quote-verbal',
            key: 'quote_verbal',
            value: 'quote_verbal',
            label: 'Verbally Quoted',
            clearable: false,
          },
          {
            className: 'change-order-status-quote-formal',
            key: 'quote_formal',
            value: 'quote_formal',
            label: 'Formally Quoted',
            clearable: false,
          },
          {className: 'change-order-status-close', key: 'close', value: 'close', label: 'Close', clearable: false},
        ]
        if (canConfirm) ret.push({
          className: 'change-order-status-confirm',
          key: 'confirm',
          value: 'confirm',
          label: 'Confirm',
          clearable: false,
        })
        return ret
      }

      case QuoteStatus.closed:
        return [{
          className: 'change-order-status-reopen',
          key: 'reopen',
          value: 'reopen',
          label: 'Re-open',
          clearable: false,
        }]

      case QuoteStatus.quoted: {
        if (!userHasPermission(user, permissions.CONFIRM_JOBS)) return []
        const ret = [
          {
            className: 'change-order-status-confirm',
            key: 'confirm',
            value: 'confirm',
            label: 'Confirm',
            clearable: false,
          },
          {
            className: 'change-order-status-reject',
            key: 'reject',
            value: 'reject',
            label: 'Reject This Revision',
            clearable: false,
          },
        ]
        if (nextRevision || revision > 1) ret.push({
          className: 'change-order-status-reject-all',
          key: 'reject_all',
          value: 'reject_all',
          label: 'Reject All Revisions',
          clearable: false,
        })
        return ret
      }

      case QuoteStatus.confirmed: {
        return [
          {
            className: 'change-order-status-deliver',
            key: 'deliver',
            value: 'deliver',
            label: 'Deliver',
            clearable: false,
            disabled: !canDeliver,
          },
          {
            className: 'change-order-status-invoice',
            key: 'invoice',
            value: 'invoice',
            label: 'Invoice',
            clearable: false,
            isDisabled: !canInvoice,
          },
          {
            className: 'change-order-status-fully-invoiced',
            key: 'fully_invoiced',
            value: 'fully_invoiced',
            label: 'Mark Fully Invoiced',
            clearable: false,
            isDisabled: !canMarkInvoiced || !canInvoiceBeforeDelivered(user),
          },
          {
            className: 'change-order-status-unconfirm',
            key: 'unconfirm',
            value: 'unconfirm',
            label: 'Un-Confirm',
            clearable: false,
            disabled: !canConfirm,
          },
        ]
      }

      case QuoteStatus.partDelivered: {
        return [
          {
            className: 'change-order-status-deliver',
            key: 'deliver',
            value: 'deliver',
            label: 'Deliver',
            clearable: false,
            isDisabled: !canDeliver,
            visible: true,
          },
          {
            className: 'change-order-status-return',
            key: 'return',
            value: 'return',
            label: 'Return',
            clearable: false,
            isDisabled: !canReturn,
            visible: returnsAvailable,
          },
          {
            className: 'change-order-status-invoice',
            key: 'invoice',
            value: 'invoice',
            label: 'Invoice',
            clearable: false,
            isDisabled: !canInvoice,
            visible: true,
          },
          {
            className: 'change-order-status-fully-invoiced',
            key: 'fully_invoiced',
            value: 'fully_invoiced',
            label: 'Mark Fully Invoiced',
            clearable: false,
            isDisabled: !canMarkInvoiced || !canInvoiceBeforeDelivered(user),
            visible: true,
          },
        ].filter(option => option.visible)
      }

      case QuoteStatus.delivered: {
        return [
          {
            className: 'change-order-status-invoice',
            key: 'invoice',
            value: 'invoice',
            label: 'Invoice',
            clearable: false,
            isDisabled: !canInvoice,
            visible: true,
          },
          {
            className: 'change-order-status-fully-invoiced',
            key: 'fully_invoiced',
            value: 'fully_invoiced',
            label: 'Mark Fully Invoiced',
            clearable: false,
            isDisabled: !canMarkInvoiced,
            visible: true,
          },
          {
            className: 'change-order-status-deliver',
            key: 'deliver',
            value: 'deliver',
            label: 'Deliver',
            clearable: false,
            isDisabled: !canDeliver,
            visible: true,
          },
          {
            className: 'change-order-status-return',
            key: 'return',
            value: 'return',
            label: 'Return',
            clearable: false,
            isDisabled: !canReturn,
            visible: returnsAvailable,
          },
        ].filter(option => option.visible)
      }

      case QuoteStatus.invoiced:
      case QuoteStatus.credited: {
        return [
          {
            className: 'change-order-status-invoice',
            key: 'invoice',
            value: 'invoice',
            label: 'Invoice',
            clearable: false,
            isDisabled: !canInvoice,
            visible: true,
          },
          {
            className: 'change-order-status-credit',
            key: 'credit',
            value: 'credit',
            label: 'Credit',
            clearable: false,
            isDisabled: !canInvoice,
            visible: true,
          },
          {
            className: 'change-order-status-paid',
            key: 'paid',
            value: 'paid',
            label: 'Mark as Paid',
            clearable: false,
            isDisabled: !canConfirm,
            visible: true,
          },
          {
            className: 'change-order-status-deliver',
            key: 'deliver',
            value: 'deliver',
            label: 'Deliver',
            clearable: false,
            isDisabled: !canDeliver,
            visible: true,
          },
          {
            className: 'change-order-status-return',
            key: 'return',
            value: 'return',
            label: 'Return',
            clearable: false,
            isDisabled: !canReturn,
            visible: returnsAvailable,
          },
        ].filter(option => option.visible)
      }

      case QuoteStatus.paid: {
        return [
          {
            className: 'change-order-status-credit',
            key: 'credit',
            value: 'credit',
            label: 'Credit',
            clearable: false,
            isDisabled: !canInvoice,
            visible: true,
          },
          {
            className: 'change-order-status-deliver',
            key: 'deliver',
            value: 'deliver',
            label: 'Deliver',
            clearable: false,
            isDisabled: !canDeliver,
            visible: true,
          },
          {
            className: 'change-order-status-return',
            key: 'return',
            value: 'return',
            label: 'Return',
            clearable: false,
            isDisabled: !canReturn,
            visible: returnsAvailable,
          },
        ].filter(option => option.visible)
      }

      case QuoteStatus.rejected: {
        return [{
          className: 'change-order-status-unreject',
          key: 'unreject',
          value: 'unreject',
          label: 'Unreject',
          clearable: false,
          isDisabled: !canConfirm,
        }]
      }

      default:
        return []
    }
  }
}

export const getAllItems = (items, mapItem = e => e) => {
  let ret = []
  items.forEach(item => {
    item = mapItem(item)
    ret.push(item)
    if (item.subItems) ret = [...ret, ...getAllItems(item.subItems, mapItem)]
  })
  return ret
}

export const findItem = (items, id) => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.id === id) return item
    if (item.subItems) {
      const ret = findItem(item.subItems, id)
      if (ret) return ret
    }
  }
  return null
}

export const findTopParent = (items, id) => {
  const item = findItem(items, id)
  if (item && item.parentId) return findTopParent(items, item.parentId)
  return item
}

export const findTopParentInSet = (itemSet, id) => {
  const item = findItem(itemSet, id)
  const parent = item && item.parentId
    ? findTopParentInSet(itemSet, item.parentId)
    : null
  return parent || item
}

export const canEnterItem = user => userHasPermission(user, permissions.ENTER_ITEM)
export const canUsePriceList = user => accountHasFeature(user, features.PRICE_LISTS_LICENCE)

// noinspection JSSuspiciousNameCombination
export const getPricingParams = ({orderItem, order, subItems, previousOrderItem}) => {
  return {
    ...getSimplePricingParams(orderItem, order),
    ...getPricingCalculatorParams(orderItem),
    ...getUnitCostAndPriceParams(orderItem, order, previousOrderItem),
    subItems: getSubItemsForPricingParams(orderItem, subItems),
  }
}

const getSubItemsForPricingParams = (orderItem, subItems) => {
  if (isNil(subItems))
    return null

  const subItemsWithParentIds = getSubItemsWithParentIds(subItems, orderItem)
  const explodedSubItems = explodeSubItems(subItemsWithParentIds)
  return explodedSubItems.map(mapSubItemForPricing)
}

const getSimplePricingParams = (orderItem, order) => {
  // noinspection JSSuspiciousNameCombination
  return {
    id: getId(orderItem.id),
    orderItemId: isUUID(orderItem.id) || orderItem.id < 0 ? null : orderItem.id,
    pricingMode: orderItem.pricingMode,
    partId: orderItem.partId,
    packId: orderItem.partPackId,
    quantity: orderItem.quantity,
    currencyRate: order.currencyRate,
    companyId: order.companyId,
    supplierId: orderItem.supplierCompanyId,
    sizeX: orderItem.length,
    sizeY: orderItem.width,
    sizeZ: orderItem.height,
    unitPrice: orderItem.unitPrice,
    unitCost: orderItem.unitCost,
  }
}

const getPricingCalculatorParams = (orderItem) => {
  if (!isPricingCalculatorEntity(orderItem))
    return {}

  return {
    pricingCalculatorPricingMethod: getPricingCalculatorMethod(orderItem),
    pricingCalculatorData: orderItem.calculatorData,
  }
}

const getUnitCostAndPriceParams = (orderItem, order, previousOrderItem) => {
  const {unitPriceSet, unitCostSet, type} = orderItem

  const typeId = get(type, 'id')
  const isEnterItem = (typeId === OrderItemType.entered)

  if (isEnterItem)
    return {
      unitPriceSet: true,
      unitCostSet: true,
      getUnitPriceIsSet: false,
      getUnitCostIsSet: false,
      previousState: null,
    }

  const getUnitPriceIsSet = !isNil(previousOrderItem) && isNil(unitPriceSet)
  const getUnitCostIsSet = !isNil(previousOrderItem) && isNil(unitCostSet)

  const previousState = !isNil(previousOrderItem) && (getUnitPriceIsSet || getUnitCostIsSet)
    ? getSimplePricingParams(previousOrderItem, order)
    : null

  return {
    unitPriceSet,
    unitCostSet,
    getUnitPriceIsSet,
    getUnitCostIsSet,
    previousState,
  }
}

export const mapSubItemForPricing = (subItem) => ({
  id: getId(subItem.id),
  parentId: getId(subItem.parentId),
  partId: subItem.partId,
  packId: subItem.partPackId,
  quantity: subItem.quantity,
  sizeX: getSubItemLength(subItem),
  sizeY: getSubItemWidth(subItem),
  sizeZ: getSubItemHeight(subItem),
  supplierId: subItem.supplierCompanyId,
  scalingType: subItem.scalingType,
  unitPrice: subItem.unitPrice,
  unitCost: subItem.unitCost,
  unitPriceSet: subItem.unitPriceSet || !subItemNeedsPricingUpdate(subItem),
  unitCostSet: subItem.unitCostSet || !subItemNeedsPricingUpdate(subItem),
  ...getPricingCalculatorParams(subItem),
})

const getId = (id) => isNil(id) || isUUID(id) ? id : formatRoundedNumber(parseInt(id), 0)

export const needsPricingUpdate = (orderItem) => {
  return isNil(orderItem.parentId) && (orderItemIsPartType(orderItem) || orderItemIsBuiltType(orderItem))
}

export const subItemNeedsPricingUpdate = (subItem) => {
  return orderItemIsPartType(subItem) || orderItemIsBuiltType(subItem)
}

export const hasNoPrice = (orderItem, subItems) => {
  return isNil(orderItem.partId) && isNil(orderItem.partPackId) && isEmpty(subItems)
}

export const requestPricing = (orderItem, order, subItems, onSuccess, onError, previousOrderItem) => {

  if (needsPricingUpdate(orderItem)) {

    if (hasNoPrice(orderItem, subItems)) {
      onSuccess({
        unitCost: 0,
        unitPrice: 0,
        totalCost: 0,
        totalPrice: 0,
      })
    } else {
      const params = getPricingParams({
        orderItem,
        order,
        subItems,
        previousOrderItem,
      })
      api.parts.getPrice(params).then(response => {
        api.handleResponseNonYield(
          response,
          (key, data) => {
            let ret
            if (data.result && data.result.length > 0) ret = data.result[0]
            onSuccess(ret)
          },
          (key, message) => {
            console.error(message)
            onError(message)
          })
      })
    }

  } else {
    onSuccess()
  }
}

export const subItemIsVariable = subItem => subItem.scalingType === ScalingType.VARIABLE.id
export const subItemIsFixed = subItem => subItem.scalingType === ScalingType.FIXED.id

export const getSubItemCost = subItem => subItem.totalCost
export const getSubItemPrice = subItem => subItem.totalPrice

export const getFixedAndVariablePricingFromAssemblyContents = (subItems) => {
  const variableCost = subItems.filter(subItemIsVariable).map(getSubItemCost).reduce((a, b) => a + b, 0)
  const variablePrice = subItems.filter(subItemIsVariable).map(getSubItemPrice).reduce((a, b) => a + b, 0)
  const fixedCost = subItems.filter(subItemIsFixed).map(getSubItemCost).reduce((a, b) => a + b, 0)
  const fixedPrice = subItems.filter(subItemIsFixed).map(getSubItemPrice).reduce((a, b) => a + b, 0)

  return {
    variableCost: round(variableCost, 2),
    variablePrice: round(variablePrice, 2),
    fixedCost: round(fixedCost, 2),
    fixedPrice: round(fixedPrice, 2),
  }
}

export const copyOrderItem = (item) => {
  return {
    ...item,
    id: uuid(),
    guid: uuid(),
    dormant: false,
    subItems: item.subItems ? item.subItems.map(copyOrderItem) : null,  // recurse
    isNew: true,
    sourceOrderItemId: isItemSaved(item) ? get(item,'id') : null,
  }
}

export const createOrderItem = (props) => {
  const {
    user,
    order,
    part = null,
    template = null,
    content = null,
    isSubItem = false,
    packTypes,
    currencies,
    defaults,
    parentId,
    templateItem,
  } = props

  let taxCode, taxCodeId, taxRate
  let referralFee = 0
  let currencyRate = 1

  if (order) {
    referralFee = (order.referralType && order.referralType.id === ReferralType.percentage)
      ? order.referralFee
      : 0
    taxCode = order.taxCode
    taxCodeId = order.taxCodeId
    taxRate = order.taxRate
    currencyRate = order.currencyRate
  }

  if (part) {
    taxCode = get(part, 'taxCode', null)
    taxCodeId = get(part, 'taxCode.id', null)
    taxRate = get(part, 'taxCode.rate', null)
  }

  if (isNil(taxCodeId)) {
    taxCodeId = get(order, 'taxCodeId', null)
    taxRate = get(order, 'taxRate', null)
  }

  let pack = null
  if (content && content.pack) {
    pack = content.pack
  } else if (part && part.packs && part.packs.length > 0) {
    if (content) {
      pack = part.packs.find(p => p.id === content.partPackId)
    } else {
      pack = part.packs.find(pack => pack.default)
    }
    pack = pack || part.packs[0]
  }

  const ret = {
    isNew: true,
    desc: createItemDescription({part}),
    discount: 0,
    dormant: false,
    margin: 0,
    markup: 0,
    netTax: 0,
    netPrice: 0,
    netMarkup: 0,
    netMargin: 0,
    nominalCode: getNominalCodeForNewOrderItem(part, template),
    departmentName: get(part, 'salesDepartmentName', ''),
    notes: get(template, 'desc', '') || get(part, 'notes', ''),
    order: null,
    orderId: null,
    parentId,
    part,
    partCode: part ? part.partCode : 'ITEM',
    partId: part ? part.id : null,
    partPackDesc: pack ? getPackTypeName(pack.packTypeId, packTypes) : 'Unit(s)',
    partPackId: pack ? pack.id : null,
    msrp: pack ? pack.msrp : null,
    partType: part ? part.type : PartType.NON_STOCK.id,
    quantity: content
      ? content.quantity
      : canUsePack(pack)
        ? getMinPackQuantity(pack)
        : 1,
    taxCode,
    taxRate,
    taxCodeId,
    totalCost: 0,
    totalPrice: 0,
    profit: 0,
    type: {id: getType(part)},
    unitCost: 0,
    unitPrice: 0,
    referralFee,
    currencyRate,
    pricingRate: getPartSaleRate(part, currencies),
    useMSRPAsPrice: false,
    supplierDiscount: 0,
    supplierCompanyId: getDefaultSupplierIdForPack(user, pack),
    ...defaults,
  }

  if (ret.partType === PartType.ASSEMBLY.id)
    ret.pricingMode = pack && pack.prices && pack.prices.length
      ? OrderItemPricingMode.part
      : OrderItemPricingMode.contents

  if (isSubItem) {
    if (content) {
      if (content.sizeX) ret.length = content.sizeX
      if (content.sizeY) {
        // noinspection JSSuspiciousNameCombination
        ret.width = content.sizeY
      }
      if (content.sizeZ) ret.height = content.sizeZ
      ret.scalingType = content.scalingType
    } else if (templateItem) {
      ret.scalingType = templateItem.scalingType
    } else {
      ret.scalingType = ScalingType.VARIABLE.id
    }
  }

  if (part) {
    if (!isSubItem || !content) {
      if (part.sizeX) ret.length = part.sizeX
      if (part.sizeY) {
        // noinspection JSSuspiciousNameCombination
        ret.width = part.sizeY
      }
      if (part.sizeZ) ret.height = part.sizeZ
    }

    if (!isSubItem) {
      if (!isNil(part.lengthClass)) ret.lengthClass = part.lengthClass
      if (!isNil(part.weight)) ret.weight = part.weight
      if (!isNil(part.weightClass)) ret.weightClass = part.weightClass
    }
  }

  return ret
}

export const getNominalCodeForNewOrderItem = (part, template) => {
  const partNominalCode = get(part, 'nominalCode', null)
  const templateNominalCode = get(template, 'nominalCode', null)

  if (!isNil(partNominalCode)) return partNominalCode
  if (!isNil(templateNominalCode)) return templateNominalCode

  return null
}

export const createItemDescription = ({part}) => {
  let ret = 'New Item'
  if (part) {
    ret = part.description
    if (part.sizeX) {
      ret += ` (${part.sizeX}`
      if (part.sizeY) ret += `x${part.sizeY}`
      if (part.sizeZ) ret += `x${part.sizeZ}`
      ret += ')'
    }
    return ret
  }
}

export const updateItemFromPart = ({
                                     user,
                                     order,
                                     orderItem,
                                     part,
                                     packTypes,
                                     currencies,
                                     isNewRow,
                                   }) => {
  orderItem.part = part
  orderItem.partId = get(part, 'id', null)
  orderItem.partCode = get(part, 'partCode', null)
  orderItem.desc = createItemDescription({part})
  orderItem.type = {id: getType(part)}
  orderItem.partType = get(part, 'type', null)
  orderItem.length = get(part, 'sizeX', null)
  // noinspection JSSuspiciousNameCombination
  orderItem.width = get(part, 'sizeY', null)
  orderItem.height = get(part, 'sizeZ', null)
  orderItem.nominalCode = getNominalCodeForNewOrderItem(part, null)
  orderItem.pricingRate = getPartSaleRate(part, currencies)
  orderItem.taxCodeId = get(part, 'taxCode.id', null)
  orderItem.taxRate = get(part, 'taxCode.rate', null)
  orderItem.notes = get(part, 'notes', '')

  const pack = part.packs ? getDefaultPack(part.packs) : null
  if (pack)
    updateItemFromPack({user, orderItem, pack, packTypes})

  if (isNil(orderItem.taxCodeId))
    orderItem.taxCodeId = get(order, 'taxCodeId', null)

  if (isNil(orderItem.taxRate))
    orderItem.taxRate = get(order, 'taxRate', null)

  if (isNewRow)
    orderItem.quantity = canUsePack(pack)
      ? getMinPackQuantity(pack)
      : get(orderItem, 'quantity', null)
}

export const updateItemFromPack = ({user, orderItem, pack, packTypes}) => {
  orderItem.partPackId = pack.id
  orderItem.partPackDesc = getPackTypeName(pack.packTypeId, packTypes)

  if (user.settings.quotingUseDefaultSupplier) {
    orderItem.supplierCompanyId = findDefaultSupplierId(pack)
  }
}

export const getItemParent = (orderItem, allItems) => {
  return allItems && orderItem.parentId
    ? allItems.find(item => item.id === orderItem.parentId) || null
    : null
}

export const getTotalQuantityForItem = (orderItem, allItems, overallParent = null) => {
  const quantity = (orderItem.enabled || orderItem.quantity > 0)
    ? orderItem.quantity
    : orderItem.content
      ? orderItem.content.quantity
      : null

  if (isNil(quantity)) return null

  if (orderItem.scalingType === PartScalingType.FIXED.id) return quantity

  if (!isNil(overallParent) && (isNil(orderItem.parentId) || orderItem.parentId === overallParent.id)) {
    return quantity * overallParent.quantity
  }

  const parent = getItemParent(orderItem, allItems)
  return parent
    ? quantity * getTotalQuantityForItem(parent, allItems, overallParent)
    : quantity
}

const pricingUpdateFields = [
  'partId',
  'partPackId',
  'quantity',
  'length',
  'width',
  'height',
  'supplierCompanyId',
  'unitPriceReset',
  'unitCostReset',
]
const itemUpdateFields = [
  'quantity',
  'markup',
  'margin',
  'discount',
  'unitPrice',
  'unitCost',
  'scalingType',
  'length',
  'width',
  'height',
  'supplierCompanyId',
]

const shouldUpdatePricing = (orderItem, editedField, isSubItemUpdate) => {
  const isPricingCalculator = isPricingCalculatorEntity(orderItem)
  const isPricingUpdateField = editedField && pricingUpdateFields.includes(editedField)
  return needsPricingUpdate(orderItem) && (isPricingUpdateField || isSubItemUpdate || isPricingCalculator)
}

// orderItem: the line item or sub-item to update
// subItems: a list of sub-items directly beneath this order item - alternatively this can
// be stored in orderItem.subItems
// order: the order this item applies to
// allItems: an array of all line items and sub-items for the applicable context (if provided will
// recursively update assemblies)
// currencies: an array of all currencies in the system
// onEdit: a function to call when asynchronously committing the change, with signature: (orderItem: object)
// onError: a function to call if an error occurs, with signature: (message: string)
// editedField: optionally specify the field that was edited (changes the behaviour of the update)
// isSubItemUpdate: specify true if this is an update to a sub-item
// returns: True if the edit was made synchronously, or False if the edit is being made asynchronously
export const updateItem = ({
                             orderItem,
                             subItems,
                             order,
                             allItems,
                             currencies,
                             onEdit,
                             onError,
                             editedField = null,
                             isSubItemUpdate = false,
                             pricingPrecision = null,
                           }) => {

  // 1. Updates requiring recalculation of pricing from the server
  if (shouldUpdatePricing(orderItem, editedField, isSubItemUpdate)) {
    const previousOrderItem = findItem(allItems, orderItem.id)

    // Immediately show the change to the user and mark the cell as 'edited'
    setEntityEdited(orderItem, editedField)
    onEdit(orderItem, null, false)

    requestPricing(
      orderItem,
      order,
      subItems || orderItem.subItems,
      pricing => {
        // Now re-calculate and commit the update
        const calculatedOrderItem =
          getCalculatedOrderItem(orderItem, currencies, editedField, pricing, null, pricingPrecision)
        onEdit(calculatedOrderItem, pricing)
      },
      onError,
      previousOrderItem)

    // Delay the update until we have a part price
    return false
  }

  // 2. Updates requiring recalculation of metrics
  const isItemUpdateField = editedField && itemUpdateFields.includes(editedField)
  if (isItemUpdateField)
    getCalculatedOrderItem(orderItem, currencies, editedField, null, null, pricingPrecision)

  // 3. Update the parent line item, if applicable
  const lineItem = orderItem.parentId ? findItem(allItems, orderItem.orderItemId) : null
  if (lineItem) {
    const isPricingUpdateField = editedField && pricingUpdateFields.includes(editedField)
    const updatedLineItem = updateSubItemTree(lineItem, [orderItem])

    const updated = isItemUpdateField || isPricingUpdateField
      ? updateItem({
        orderItem: updatedLineItem,
        order,
        allItems,
        currencies,
        onEdit,
        onError,
        isSubItemUpdate: true,
        pricingPrecision,
      })
      : true

    if (updated) onEdit(updatedLineItem)
    return updated
  }

  // This was a synchronous edit
  return true
}

export const addItemParents = (itemOrItems) => itemOrItems.map
  ? itemOrItems.map(item => recordItemParent(item))
  : recordItemParent(itemOrItems)

export const removeItemParents = (itemOrItems) => itemOrItems.map
  ? itemOrItems.map(deleteItemParent)
  : deleteItemParent(itemOrItems)

const recordItemParent = (item, parent) => ({
  ...item,
  parent,
  subItems: item.subItems ? item.subItems.map(subItem => recordItemParent(subItem, item)) : null,
})

const deleteItemParent = (item) => ({
  ...item,
  parent: null,
  subItems: item.subItems ? item.subItems.map(deleteItemParent) : null,
})

// Check if the given pricing template is appropriate populated in subItems
export const canSubmitTemplate = (template, subItems) => {
  // Check one enabled sub-item exists for each mandatory template item
  return !template.items || template.items.reduce((cur, item) =>
    cur && (!item.mandatory || subItems.some(subItem =>
        subItem.enabled &&
        subItem.templateItem &&
        subItem.templateItem.id === item.id)
    ), true)
}

export const assemblyHasNestedCalcWizard = (item) => {
  if (item.subItems) {
    return item.subItems.some(si => {
      if (
        si.partType === PartType.PRO_CALC_WIZARD.id
        || si.partType === PartType.GO_PRICING_CALCULATOR.id
      ) return true
      return !!(si.subItems && assemblyHasNestedCalcWizard(si))

    })
  }
  return false
}

export const appendDetailFromSubItems = (desc, subItems) => {
  let ret = desc
  const items = subItems ? subItems.filter(item => item.enabled) : []
  if (items && items.length > 0) {
    if (ret.length > 0) ret += '\r\n'
    ret += items.map(createSubItemDetailText).join('\r\n')
  }
  return ret
}

export const createSubItemDetailText = (subItem) => {
  return `${subItem.quantity} x ${subItem.desc}`
}

export const getDormantOrderItems = (orderItems) => {
  return isEmpty(orderItems)
    ? []
    : orderItems.filter(orderItem => get(orderItem, 'dormant', false) === true)
}

export const getNonDormantOrderItems = (orderItems) => {
  return isEmpty(orderItems)
    ? []
    : orderItems.filter(orderItem => get(orderItem, 'dormant', false) === false)
}

export const isAnyOrderItemHavingAJobCard = (orderItems) => {
  return isEmpty(orderItems)
    ? false
    : orderItems.some(orderItem => !isNil(get(orderItem, 'jobCard', null)))
}

export const isAnyOrderItemHavingADeliveryNote = (orderItems, deliveryNotes) => {

  return orderItems.some(orderItem => {
    const orderItemId = get(orderItem, 'id', null)

    return deliveryNotes
      .filter(deliveryNote =>
        !deliveryNote.returnNote)
      .flatMap(deliveryNote => deliveryNote.deliveryNoteItems)
      .some(deliveryNoteItem =>
        deliveryNoteItem.orderItemId === orderItemId &&
        deliveryNoteItem.quantity > 0
      )
  })
}

export const isDeletingAllOrderItems = (orderItems, orderItemsToDormant) => {
  const currentDormantOrderItems = getDormantOrderItems(orderItems)
  const totalOrderItems = orderItems.length
  const totalCurrentDormantItems = currentDormantOrderItems.length
  const totalOrderItemsToDormant = orderItemsToDormant.length

  return (totalCurrentDormantItems + totalOrderItemsToDormant) >= totalOrderItems
}

export const getTotalOrderItemsQuantity = (orderItems) => {
  return orderItems.reduce((total, item) => total + get(item, 'quantity', 0), 0)
}

export const getTotalOrderItemsUnitCost = (orderItems) => {
  return orderItems.reduce((total, item) => total + get(item, 'unitCost', 0), 0)
}

export const hasAnyDelivery = (order) =>
  [
    DeliveryStatus.partDelivered,
    DeliveryStatus.fullyDelivered,
  ].includes(get(order, 'deliveryStatus.id', null))

export const getOrderType = (order, orderTypes) => {
  const orderTypeId = get(order, 'typeId', null)
  return orderTypes.find(orderType => orderType.id === orderTypeId)
}

export const getActiveSubItems = (subItems) => {
  return subItems
    .filter(isSubItemActive)
    .map(getItemWithActiveSubItems)
}

export const getItemWithActiveSubItems = (item) => {
  return {
    ...item,
    subItems: isNil(item.subItems)
      ? null
      : getActiveSubItems(item.subItems),
  }
}

export const isSubItemActive = (subItem) => {
  return !subItem.deleted && (isNil(subItem.enabled) || subItem.enabled)
}

export const getAllSubItemsForOrderItems = (orderItems) => {
  return flatten(orderItems.map(getAllSubItems))
}

export const explodeSubItems = (subItems) => {
  return subItems ? flatten(subItems.map(subItem => {
    return [
      subItem,
      ...getAllSubItems(subItem),
    ]
  })) : []
}

export const getSubItemsWithParentIds = (subItems, parentOrderItem) => {
  return subItems.map(subItem => {
    return {
      ...subItem,
      parentId: isNil(subItem.parentId)
        ? parentOrderItem.id
        : subItem.parentId,
      subItems: subItem.subItems
        ? getSubItemsWithParentIds(subItem.subItems, subItem)
        : undefined,
    }
  })
}

export const getAllSubItems = (orderItem) => {
  const subItems = get(orderItem, 'subItems', [])

  return subItems ? flatten(
    subItems.map(subItem => ([
      subItem,
      ...getAllSubItems(subItem),
    ]))) : []
}

export const getSubItemsForParent = (subItems, parentId) => {
  return subItems.filter(subItems => subItems.parentId === parentId)
}

export const getUpdatedOrderItemForSubItemUpdate = (subItem, allItems) => {
  if (subItem.parentId) {
    const orderItem = allItems.find(orderItem => orderItem.id === subItem.orderItemId)
    if (orderItem && orderItem.id !== subItem.id) {
      return updateSubItemTree(orderItem, [subItem])
    }
  }
  return null
}

export const getUpdatedParentForSubItemUpdate = (subItem, allSubItemsAtHighestLevel) => {
  if (subItem.parentId !== subItem.orderItemId) {
    const allSubItems = explodeSubItems(allSubItemsAtHighestLevel)
    const topParent = findTopParentInSet(allSubItems, subItem.id)
    if (topParent && topParent.id !== subItem.id) {
      return updateSubItemTree(topParent, [subItem])
    }
  }
  return null
}

export const updateSubItemTree = (orderItem, updatedSubItems) => {
  const updatedItem = updatedSubItems.find(subItem => subItem.id === orderItem.id) || orderItem

  return orderItem.subItems
    ? {
      ...updatedItem,
      subItems: orderItem.subItems.map(subItem => updateSubItemTree(subItem, updatedSubItems)),
    }
    : updatedItem
}

export const replaceSubItemTree = (parentItem, newSubItems) => {
  const newParent = newSubItems.find(subItem => subItem.id === parentItem.id) || parentItem

  return {
    ...newParent,
    subItems: newSubItems
      .filter(subItem => subItem.parentId === parentItem.id)
      .map(subItem => replaceSubItemTree(subItem, newSubItems)),
  }
}

export const getToOrderQuantityForOrderItem = (orderItem, allItems) => {
  const totalQuantity = getTotalQuantityForItem(orderItem, allItems)
  const orderedQuantity = getPurchaseOrderedQuantityForOrderItem(orderItem)
  return Math.max(totalQuantity - orderedQuantity, 0)
}

export const getTemplateOptions = (type, companyTemplate, defaultTemplate, noun, entity, textTemplates) => {
  const companyName = get(entity, 'company.name', 'This Company')
  const ret = QuoteHelper.getTextTemplateOptionsByType(textTemplates, type)
  if (companyTemplate) ret.unshift({key: -1, label: `Default ${noun} For ${companyName}`})
  if (defaultTemplate) ret.unshift({key: -2, label: `Default ${noun}`})
  return ret
}

export const getTemplateText = (id, companyTemplate, defaultTemplate, textTemplates) => {
  if (id === -2) return defaultTemplate
  if (id === -1) return companyTemplate

  const template = textTemplates.find(t => t.id === id)
  if (template) return template.text
  return null
}

export const hasPartPackForSuppliers = (orderItem, isComplexPricingEnabled) => {
  return !isNil(getPartPackForSuppliers(orderItem, isComplexPricingEnabled))
}

export const getPartPackForSuppliers = (orderItem, isComplexPricingEnabled) => {
  const part = get(orderItem, 'part', null)
  const partPacks = getPartPacks(part, isComplexPricingEnabled)
  const pack = getPartPackFor(orderItem.partPackId, partPacks)
  return partPackHasSuppliers(pack) ? pack : null
}

export const getPartPackOptionForOrderItem = (orderItem) => {
  const partSupplier = getPartSupplierForCompany(orderItem.part, orderItem.partPackId, orderItem.supplierCompanyId)
  return partSupplier ? getPartPackSupplierOption(partSupplier) : null
}

export const getSupplierCompanyNameForOrderItem = (orderItem) => {
  const partSupplier = getPartSupplierForCompany(orderItem.part, orderItem.partPackId, orderItem.supplierCompanyId)
  return get(partSupplier, 'company.name', '')
}

export const getPricingFromAssemblyContents = (orderItem, allItems, lineItem) => {
  const subItems = getSubItemsForParent(allItems, orderItem.id)

  let totalCost = 0, totalPrice = 0
  subItems.forEach(subItem => {
    const quantity = getTotalQuantityForItem(subItem, allItems, lineItem)
    if (!isNil(quantity)) {
      totalCost += (subItem.unitCost * quantity)
      totalPrice += (subItem.unitPrice * quantity)
    }
  })

  return {
    unitCost: round(totalCost / orderItem.quantity, 10),
    unitPrice: round(totalPrice / orderItem.quantity, 10),
    totalCost: round(totalCost, 10),
    totalPrice: round(totalPrice, 10),
  }
}

export const onCanSelectOrderItem = (user, orderItem) => {
  if (isSubItem(orderItem))
    return false

  if (isPricingCalculatorEntity(orderItem)) {
    return isPricingCalculatorActive(user, getCalculatorNameForItem(orderItem))
  }

  return true
}

export const onCanSelectOrderItemInSubItemGrid = (user, orderItem) => {
  return isPricingCalculatorEntity(orderItem) && isPricingCalculatorActive(user, getCalculatorNameForItem(orderItem))
}

export const getTotalPriceForOrderItem = (orderItem, allItems) => {
  return isSubItem(orderItem)
    ? getTotalValue(orderItem.unitPrice, getTotalQuantityForItem(orderItem, allItems))
    : orderItem.totalPrice
}

export const getTotalCostForOrderItem = (orderItem, allItems) => {
  return isSubItem(orderItem)
    ? getTotalValue(orderItem.unitCost, getTotalQuantityForItem(orderItem, allItems))
    : orderItem.totalCost
}

export const getNetPriceForOrderItem = (orderItem, allItems) => {
  return isSubItem(orderItem)
    ? getTotalPriceForOrderItem(orderItem, allItems)
    : orderItem.netPrice
}

export const isSubItem = (orderItem) => {
  return !isNil(orderItem.parentId)
}

export const getLineItemFromDependency = (orderItem) => {
  return isSubItem(orderItem)
    ? orderItem?.orderItem
    : orderItem
}

export const updateItems = (currentItems, updatedItems) => {
  return updatedItems.map(updatedItem => {
    const updatedItemGuid = get(updatedItem, 'guid', 'default-updated-guid').toLowerCase()
    const currentItem = currentItems.find(currentItem => {
      const currentItemGuid = get(currentItem, 'guid', 'default-current-guid').toLowerCase()
      return currentItemGuid === updatedItemGuid
    })
    return isNil(currentItem)
      ? updatedItem
      :
      {
        ...updatedItem,
        unitPriceSet: currentItem.unitPriceSet,
        unitCostSet: currentItem.unitCostSet,
        subItems: isNil(currentItem.subItems) || isNil(updatedItem.subItems)
          ? updatedItem.subItems
          : updateItems(currentItem.subItems, updatedItem.subItems),
      }
  })
}

export const showUnitPriceIsSet = (orderItem) => {
  if (get(orderItem, 'type.id') === OrderItemType.pricing_calculator) return false
  return orderItem.unitPriceSet && (orderItemIsPartType(orderItem) || orderItemIsBuiltType(orderItem))
}

export const showUnitCostIsSet = (orderItem) => {
  if (get(orderItem, 'type.id') === OrderItemType.pricing_calculator) return false
  return orderItem.unitCostSet && (orderItemIsPartType(orderItem) || orderItemIsBuiltType(orderItem))
}

export const isOrderReasonMandatory = (user) => {
  return get(user.settings.orderConfirmRequiredFields, 'required.ORDER_CONFIRM_REASON', false)
}

export const getOrderReasonName = (order, key, type, orderReasons) => {
  const orderReasonId = get(order, key, orderReasons)
  const orderReason = orderReasons.find(orderReason => {
    return (
      orderReason.id === orderReasonId &&
      orderReason.type.id === type
    )
  })
  return get(orderReason, 'name', null)
}

export const getOrderOption = (order) => {
  const status = get(order, 'status.label', '')
  return isNil(order)
    ? null
    : {
      value: order.id,
      label: `${order.ref}: ${order.desc}`,
      order,
      status,
      className: `order-${parseCssSelector(status)}`,
    }
}

export const isShowQuoteMarginWarning = (belowMinMargin, hasAcceptedWarnings, jobStatus) => {
  switch (jobStatus) {
    case 'quote_verbal':
    case 'quote_formal':
    case 'confirm':
      return belowMinMargin && !hasAcceptedWarnings
    default:
      return false
  }
}

export const isAllowAutoCommitForJobStatus = (jobStatus) => {
  switch (jobStatus) {
    case 'paid':
      return false
    default:
      return true
  }
}

export const searchQuotes = () => (search) => {
  if (search && search.length > 2) {
    return api.quote.get(
      {
        search,
        sortColumn: 'createdDate',
        sortDirection: 'desc',
        status: [
          QuoteStatus.draft,
          QuoteStatus.quoted,
          QuoteStatus.confirmed,
          QuoteStatus.partDelivered,
          QuoteStatus.delivered,
          QuoteStatus.invoiced,
          QuoteStatus.credited,
          QuoteStatus.paid,
        ],
      }, {
        default: true,
      }).then(response => {
      let ret = []
      api.handleResponseNonYield(
        response,
        (key, data) => {
          ret = data.result
        },
        (key, message) => {
          console.error(message)
        })

      return ret.map(order => {
        const status = get(order, 'status.label', '')
        return ({
          value: order.id,
          label: `${order.ref}: ${order.desc}`,
          order,
          status,
          className: `order-${parseCssSelector(status)}`,
        })
      })
    })
  }

  return []
}

export const hasCalcWizards = (orderItems) => {
  return orderItems.some(item => get(item, 'type.id') === OrderItemType.calc_wizard)
}

export const hasUnlicensedPricingCalculators = (user, orderItems) => {
  return orderItems
    .filter(isPricingCalculatorEntity)
    .some(item => !isPricingCalculatorActive(user, getCalculatorNameForItem(item)))
}

export const isItemCalcWizard = (orderItem) => {
  return get(orderItem, 'type.id') === OrderItemType.calc_wizard
}

export const createCopyableItems = (user, orderItems) => {
  return orderItems
    .filter(orderItem => isItemCopyable(user, orderItem))
    .map(orderItem => {
      return {
        ...orderItem,
        subItems: createCopyableItems(user, orderItem.subItems || []),
      }
    })
}

export const isItemCopyable = (user, orderItem) => {
  if (isItemCalcWizard(orderItem))
    return false

  if (isPricingCalculatorEntity(orderItem))
    return isPricingCalculatorActive(user, getCalculatorNameForItem(orderItem))

  return true
}

export const getItemsWithSelectedDuplicated = (allItems, selectedItems) => {
  return allItems.map(item => {
    return selectedItems.some(selectedItem => selectedItem.id === item.id)
      ? [item, copyOrderItem(item)]
      : item
  }).flat()
}

export const getItemsWithUpdates = (filteredItems, updatedItems) => {
  return filteredItems.map(item => {
    const updatedItem = updatedItems.find(updatedItem => updatedItem.id === item.id)
    const updatedMainItem = updatedItem ? {...item, ...updatedItem} : item
    if (!Array.isArray(item.subItems)) {
      return updatedMainItem
    }
    const updatedSubItems = item.subItems.map(subItem => {
      const updatedSubItem = updatedItems.find(updatedItem => updatedItem.id === subItem.id)
      return updatedSubItem || subItem
    })
    return {
      ...updatedMainItem,
      subItems: updatedSubItems,
    }
  })
}

export const isAnySubItemSelected = (subItems, selectedItemIds) => {
  if (isEmpty(subItems)) return false

  return subItems.some(subItem => {
      const subItemSubItems = get(subItem, 'subItems')
      return selectedItemIds.includes(subItem.id) || isAnySubItemSelected(subItemSubItems, selectedItemIds)
    }
  )
}

export const isBulkEditActionEnabled = (user, actionName, orderItems, selectedItemIds) => {
  const selectedItems = orderItems.filter(orderItem => selectedItemIds.includes(orderItem.id))

  if (actionName !== 'remove' && hasUnlicensedPricingCalculators(user, selectedItems))
    return false

  if (actionName === 'revert')
    return orderItems.some(orderItem => isAnySubItemSelected(get(orderItem, 'subItems'), selectedItemIds))

  return !isEmpty(selectedItems)
}

export const getSubItemsCostReverted = (subItems, selectedItemIds) => {
  return subItems.map(subItem => {
    const subItemSubItems = get(subItem, 'subItems', [])
    return ({
      ...subItem,
      unitCostSet: selectedItemIds.includes(subItem.id) ? false : subItem.unitCostSet,
      subItems: !isEmpty(subItemSubItems)
        ? getSubItemsCostReverted(subItemSubItems, selectedItemIds)
        : [],
    })
  })
}

export const getItemsCostReverted = (item, selectedItemIds) => {
  const subItems = get(item, 'subItems', [])
  return {
    ...item,
    unitCostSet: selectedItemIds.includes(item.id) ? false : item.unitCostSet,
    subItems: getSubItemsCostReverted(!isEmpty(subItems) ? subItems : [], selectedItemIds),
  }
}

export const getSubItemsPriceReverted = (subItems, selectedItemIds) => {
  return subItems.map(subItem => {
    const subItemSubItems = get(subItem, 'subItems', [])
    return ({
      ...subItem,
      unitPriceSet: selectedItemIds.includes(subItem.id) ? false : subItem.unitPriceSet,
      subItems: !isEmpty(subItemSubItems)
        ? getSubItemsPriceReverted(subItemSubItems, selectedItemIds)
        : [],
    })
  })
}

export const getItemsPriceReverted = (item, selectedItemIds) => {
  const subItems = get(item, 'subItems', [])
  const updatedItem = {
    ...item,
    unitPriceSet: selectedItemIds.includes(item.id) ? false : item.unitPriceSet,
    subItems: getSubItemsPriceReverted(!isEmpty(subItems) ? subItems : [], selectedItemIds),
  }

  if (isPricingCalculatorEntity(updatedItem) && selectedItemIds.includes(item.id))
    setPricingMethod(updatedItem, PRICING_METHODS.AUTO_CALCULATED)

  return updatedItem
}

export const getSubItemsCostAndPriceReverted = (subItems, selectedItemIds) => {
  return subItems.map(subItem => {
    const subItemSubItems = get(subItem, 'subItems', [])
    return ({
      ...subItem,
      unitCostSet: selectedItemIds.includes(subItem.id) ? false : subItem.unitCostSet,
      unitPriceSet: selectedItemIds.includes(subItem.id) ? false : subItem.unitPriceSet,
      subItems: !isEmpty(subItemSubItems)
        ? getSubItemsCostAndPriceReverted(subItemSubItems, selectedItemIds)
        : [],
    })
  })
}

export const getItemsCostAndPriceReverted = (item, selectedItemIds) => {
  const subItems = get(item, 'subItems', [])
  return {
    ...item,
    unitCostSet: selectedItemIds.includes(item.id) ? false : item.unitCostSet,
    unitPriceSet: selectedItemIds.includes(item.id) ? false : item.unitPriceSet,
    subItems: getSubItemsCostAndPriceReverted(!isEmpty(subItems) ? subItems : [], selectedItemIds),
  }
}

export const getJobBoardOption = (e, productionCompleteName) => {
  let jobCards = get(e, 'jobCards', [])
  jobCards = isNil(jobCards) ? [] : jobCards

  const jobStatus = get(e, 'jobStatus', null)
  const totalJobCards = jobCards.length
  const totalCompletedJobCards = jobCards.filter(jobCard => isJobCardComplete(e, jobCard)).length
  const colorForJobCardStatus = getColorForJobCardStatus(theme, jobStatus)

  if (totalJobCards > 1 && (jobStatus === JobBoardStatus.wip || jobStatus === JobBoardStatus.onHold)) {
    const completedJobCardsLabel = `${totalCompletedJobCards} / ${totalJobCards} Job Cards`
    return {
      statusColor: colorForJobCardStatus,
      label: completedJobCardsLabel,
    }
  }

  return {
    statusColor: colorForJobCardStatus,
    label: !isNil(productionCompleteName) && jobStatus === JobBoardStatus.complete
      ? productionCompleteName
      : getJobBoardStatusName(jobStatus),
  }
}

export const getReceivedForOrderItem = (orderItem) => {
  const {purchaseOrderItems = []} = orderItem
  return purchaseOrderItems.reduce((acc, item) => {
    const received = get(item, 'quantityReceived', 0)
    const returned = get(item, 'quantityReturned', 0)
    return acc + received - returned
  }, 0)
}

export const getPricingCalculatorMethod = (item) => {
  const inputs = get(item, 'calculatorData.inputs', [])
  const pricingMethodInput = inputs.find(input => input.key === 'pricingMethod')
  return get(pricingMethodInput, 'textValue', 'autoCalculated')
}

export default {
  PriceMode,
  OrderItemType,
  OrderItemPricingMode,
  getAllScalingTypes,
  recalculateTaxRate,
  showSubItems,
  round,
  getPercentage,
  getDiscount,
  getPercentageReduction,
  getLineItemTotalPrice,
  getLineItemsTotalPrice,
  getLineItemTax,
  getLineItemsTotalTax,
  getLineItemsTotalPriceIncludingTax,
  getLineItemsTotalCost,
  getLineItemsMargin,
  getLineItemsProfit,
  getMargin,
  getNetMargin,
  getOrderReasons,
  getRejectedOrderReasons,
  getOrderStatusOptions,
  canEditItem,
  canAddItemOfType,
  canAddItem,
  canAddItemFromPricingTemplate,
  getType,
  getPricingParams,
  requestPricing,
  getPricingCurrencyRate: getPartSaleRate,
  copyOrderItem,
  createOrderItem,
  createItemDescription,
  canEnterItem,
  canUsePriceList,
  updateItemFromPart,
  updateItemFromPack,
  updateItem,
  addItemParents,
  removeItemParents,
  getAllItems,
  findItem,
  findTopParent,
  reorderItems,
  canSubmitTemplate,
  assemblyHasNestedCalcWizard,
  appendDetailFromSubItems,
  createSubItemDetailText,
}
